import type { PostHog } from 'posthog-js';

import { SLEEPIO_CANOPY_PROGRAM } from './posthog';

/**
 * The six sign-in events and the one person property, in one place.
 *
 * They are gathered here rather than sprinkled through the screens because a
 * dashboard is being built against these exact names on a separate ticket, and a
 * typo in one component would show up as a silently missing funnel step rather than
 * as a failure. Every name below is asserted in this module's tests.
 *
 * **`SignInFailed` cannot say whether the password was wrong or was never set**, and
 * that is a deliberate limitation rather than an oversight. The two are
 * indistinguishable at the call, so `wrong_credentials` covers both. Splitting them
 * would reveal whether an address belongs to a member, which is the reason the two
 * cases share one screen — so do not add a reason that separates them.
 *
 * **Nothing here records whether an account has a password, deliberately.** An earlier
 * design measured that, back when new and existing members got passwords by different
 * routes; one flow now serves everyone, so it stopped answering a question anyone
 * asks. It could not have been made trustworthy either — Supabase fills the underlying
 * column in for every account it creates, password or not, so the measure could never
 * be backfilled and a missing value would read as "no password" when it really means
 * "never observed". We accept not knowing how many members hold one. That is a trade
 * that was made on purpose, not a gap to close with a helpful extra event.
 */

export const SIGN_IN_EVENTS = {
  landed: 'SignInLanded',
  methodSelected: 'SignInMethodSelected',
  submitted: 'SignInSubmitted',
  succeeded: 'SignInSucceeded',
  failed: 'SignInFailed',
  abandoned: 'SignInAbandoned',
} as const;

export const PERSON_PROPERTIES = {
  preferredSignInMethod: 'preferred_sign_in_method',
} as const;

/** How the member is signing in. */
export type SignInMethod = 'password' | 'magic_link' | 'otp';

/**
 * Why a sign-in was refused.
 *
 * `wrong_credentials` deliberately merges a wrong password with an account that has
 * no password. That merge is not a privacy measure — analytics is internal — it is
 * there because the two genuinely cannot be told apart at the call.
 */
export type SignInFailureReason =
  | 'wrong_credentials'
  | 'not_entitled'
  | 'not_activated'
  | 'rate_limited'
  | 'unknown';

type Client = PostHog | undefined;

const base = { program: SLEEPIO_CANOPY_PROGRAM };

/**
 * Measurement must never be able to break a sign-in.
 *
 * Every call below goes through here, so a missing method on an older client, an ad
 * blocker replacing the client with a stub, or a throw from inside the library costs
 * a lost event rather than a member who cannot get in. This is not defensiveness for
 * its own sake — an early version of this work took the whole magic-link callback
 * down because the client in play had no `setPersonProperties`.
 */
const safely = (run: () => void): void => {
  try {
    run();
  } catch {
    // Intentionally silent: a failed measurement is not worth a log line on a path
    // this hot, and certainly not worth failing the sign-in it is measuring.
  }
};

export const trackSignInLanded = (posthog: Client): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.landed, base));
};

export const trackSignInMethodSelected = (posthog: Client, method: SignInMethod): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.methodSelected, { ...base, method }));
};

export const trackSignInSubmitted = (posthog: Client): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.submitted, base));
};

export const trackSignInSucceeded = (posthog: Client): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.succeeded, base));
};

export const trackSignInFailed = (posthog: Client, reason: SignInFailureReason): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.failed, { ...base, failure_reason: reason }));
};

/**
 * The member left without a session.
 *
 * Sent instantly over a beacon because it usually fires as the page is going away,
 * and a queued event would not survive that.
 */
export const trackSignInAbandoned = (posthog: Client): void => {
  safely(() => posthog?.capture?.(SIGN_IN_EVENTS.abandoned, base, {
    send_instantly: true,
    transport: 'sendBeacon',
  }));
};

export const setPreferredSignInMethod = (posthog: Client, method: SignInMethod): void => {
  safely(() => posthog?.setPersonProperties?.({
    [PERSON_PROPERTIES.preferredSignInMethod]: method,
  }));
};
